import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { getCard, saveStatistic } from '@/api/api'
import { FeedPostNotification } from '@/feed/Feed.interface'
import FeedPostBack from '@/components/FeedPostBack'
import Loading from '@/components/common/Loading'
import CardNotFound from '@/components/error/NotFound'
import FailedToFetch from '@/components/error/FailedToFetch'
import BrandedToolbar, {
  isToolbarHidden
} from '@/components/Toolbar/BrandedToolbar'
import useBranding from '@/lib/useBranding'
import usePageParams from '@/lib/usePageParams'

export default function PostBackRoute() {
  const { id = '' } = useParams<{ id: string }>()
  const pageParams = usePageParams()
  const { branding, isLoading: isBrandingLoading } = useBranding(pageParams)

  const [feedPost, setFeedPost] = useState<FeedPostNotification | null>(null)
  const [isCardLoading, setIsCardLoading] = useState(true)
  const [isNotFound, setIsNotFound] = useState(false)
  const [hasServerError, setHasServerError] = useState(false)

  // See PostFrontRoute: guards against the effect re-running, not against a
  // genuine re-navigation to the same card.
  const impressionSentForRef = useRef<string | null>(null)

  const { alias, sdkAppId, sdkAppKey } = pageParams
  const key = `${sdkAppId}${sdkAppKey}`

  useEffect(() => {
    let cancelled = false
    setIsCardLoading(true)
    setIsNotFound(false)
    setHasServerError(false)

    // Deliberately isolated from the card fetch. The Next version let a failed
    // stats call fall into the same catch and render "Failed to load feed post"
    // over a card that had loaded fine; the front page never did that.
    const sendImpression = async () => {
      if (impressionSentForRef.current === id) return
      impressionSentForRef.current = id

      try {
        await saveStatistic({
          key,
          data: {
            campaignGuid: id,
            alias,
            key: 'card_back_impression'
          },
          additionalHeaders: {
            source: 'native'
          }
        })
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    }

    const loadCard = async () => {
      try {
        const response = await getCard({
          params: {
            guid: id,
            alias
          },
          key,
          additionalHeaders: {
            source: 'native'
          }
        })

        if (!cancelled) {
          setFeedPost(response.data)
        }

        if (!response.data.back || !Array.isArray(response.data.back)) {
          if (!cancelled) setHasServerError(true)
        } else {
          sendImpression()
        }
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          if (!cancelled) setIsNotFound(true)
        } else {
          // eslint-disable-next-line no-console
          console.error(error)
          if (!cancelled) setHasServerError(true)
        }
      } finally {
        if (!cancelled) {
          setIsCardLoading(false)
        }
      }
    }

    loadCard()

    return () => {
      cancelled = true
    }
  }, [id, alias, key])

  if (isBrandingLoading || isCardLoading) {
    return <Loading />
  }

  const hideToolbar = isToolbarHidden(branding)

  if (isNotFound) {
    return (
      <>
        <BrandedToolbar branding={branding} />
        <div className={`${hideToolbar ? 'pt-0' : 'pt-[55px]'}`}>
          <CardNotFound
            withBackButton={
              branding?.enabled && !branding?.feedHideGoBackToMainButton
            }
          />
        </div>
      </>
    )
  }

  if (hasServerError || !feedPost || !feedPost.back) {
    return (
      <>
        <BrandedToolbar branding={branding} />
        <div className={`${hideToolbar ? 'pt-0' : 'pt-[55px]'}`}>
          <FailedToFetch text="Failed to load feed post" />
        </div>
      </>
    )
  }

  return (
    <>
      <BrandedToolbar branding={branding} />
      {/*
        Carried over from the Next version: this padding is unconditional, so a
        branded app with feedHideToolbar still gets 55px of leading space here.
        Left as-is to keep the migration behaviour-neutral.
      */}
      <div className="pt-[55px]">
        <FeedPostBack
          id={id}
          pageParams={pageParams}
          feedPostBack={feedPost.back}
        />
      </div>
    </>
  )
}
